import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { companyState } from '../../lib/companySession';

/*
 * The third company state, and the only one that is not a gate.
 *
 * An active company missing name, website or description fails isCompanyComplete on the server,
 * which 400s profile_incomplete on exactly two routes — POST /api/hr/jobs/:id/generate-tasks and
 * POST /api/hr/jobs/:id/publish (both throws are in routes/hrJobs.ts; grep 'profile_incomplete'
 * rather than trusting a line number here, which is how the previous two in this comment went
 * stale). Everything else about the HR app works, so blocking on it would be a lie; saying
 * nothing until a publish fails would be worse. This says it in place, above the page, from
 * inside the Layout.
 *
 * IT READS THE PICKED TENANT (`activeCompany`), NOT `company`. Those two routes are called with
 * ?companyId=<the picked one>; /api/me's `company` is membership row 0 under every id, so a
 * recruiter on two hiring teams who switched to an incomplete second employer got silence here
 * and 400 profile_incomplete there. When it does fire it now names the employer the publish
 * would actually have been for.
 *
 * Renders nothing at all in the other three states, and nothing on /hr/profile itself — that is
 * the page carrying the form that fixes it, and a banner pointing at the page you are already on
 * is noise.
 */
const CompanyIncompleteNotice: React.FC = () => {
  const { user, activeCompany } = useAuth();
  const { pathname } = useLocation();

  if (user?.userType !== 'hr') return null;
  if (companyState(activeCompany) !== 'incomplete') return null;
  if (pathname === '/hr/profile') return null;

  return (
    <div
      className="border border-hairline rounded-md px-5 py-4 flex items-start justify-between gap-4 flex-wrap"
      style={{ backgroundColor: 'var(--color-saffron-tint)' }}
    >
      <div className="min-w-0">
        <span className="label-editorial block mb-1">Company profile incomplete</span>
        <p className="text-sm text-ink-secondary leading-relaxed max-w-2xl">
          {activeCompany?.name || 'Your company'} is missing its name, website or description. You can
          draft jobs without them, but publishing one and letting the AI draft its tasks both need
          all three — they are the whole of what it reads about your company.
        </p>
      </div>
      <Link to="/hr/profile" className="btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.8125rem' }}>
        Finish it
      </Link>
    </div>
  );
};

export default CompanyIncompleteNotice;
